'use client';

import { useQuery } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useContext, useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { CartContext } from '@/entities/cart';
import { isDeliveryMethod } from '@/entities/venue/lib/isDeliveryMethod';
import { DeliveryType } from '@/features/enums/DeliveryType';
import { QueryApi } from '@/features/query/api';

const PARAM_KEYS = ['street', 'city', 'postCode', 'country', 'lat', 'lon', 'deliveryType'] as const;

/**
 * When true, an address passed in the URL always overwrites the cart address — even if the venue already
 * has one (hydrated from localStorage). This makes selecting the CURRENT venue on the venue router apply
 * the freshly entered address. Set to false to restore the previous guard (URL address used only when the
 * cart has none).
 */
const ALWAYS_OVERWRITE_ADDRESS_FROM_URL = true;

export const usePrefillCartFromUrl = () => {
    const cart = useContext(CartContext);
    const searchParams = useSearchParams();
    const router = useRouter();
    const pathname = usePathname();
    const appliedRef = useRef(false);
    const deliveryMethodsQuery = useQuery(QueryApi.DeliveryMethods.queries.DeliveryMethods);
    const isHydrated = useWatch({
        control: cart?.formMethods?.control,
        name: '_isHydrated',
    });

    useEffect(() => {
        if (
            appliedRef.current ||
            !cart?.formMethods ||
            !isHydrated ||
            searchParams === null ||
            pathname === null
        ) {
            return;
        }

        const street = searchParams.get('street');
        const deliveryType = searchParams.get('deliveryType') as DeliveryType;
        if (!street && !deliveryType) return;

        // Honoring the delivery-type hint needs the method list; wait until it's loaded.
        if (deliveryType && !deliveryMethodsQuery.data) return;

        appliedRef.current = true;

        if (street) {
            const existingAddress = cart.formMethods.getValues('deliveryMethod.address');
            if (ALWAYS_OVERWRITE_ADDRESS_FROM_URL || !existingAddress?.street) {
                cart.formMethods.setValue('deliveryMethod.address', {
                    street,
                    city: searchParams.get('city') ?? '',
                    postCode: searchParams.get('postCode') ?? '',
                    country: searchParams.get('country') ?? '',
                    lat: searchParams.get('lat') ?? '',
                    lon: searchParams.get('lon') ?? '',
                });
                cart.formMethods.setValue('deliveryMethod.addressId', null);
                cart.formMethods.setValue('deliveryMethod.manual', true);
            }
        }

        if (deliveryType === DeliveryType.DELIVERY || deliveryType === DeliveryType.PICKUP) {
            const wantDelivery = deliveryType === DeliveryType.DELIVERY;
            const method = deliveryMethodsQuery.data?.find((m) => isDeliveryMethod(m) === wantDelivery);
            if (method) {
                cart.formMethods.setValue('deliveryMethod.id', method.id);
                cart.formMethods.setValue('deliveryMethod.type', deliveryType);
            }
        }

        const next = new URLSearchParams(searchParams);
        PARAM_KEYS.forEach((k) => next.delete(k));
        const q = next.toString();
        router.replace(q ? `${pathname}?${q}` : pathname, { scroll: false });
    }, [cart, searchParams, router, pathname, isHydrated, deliveryMethodsQuery.data]);
};
